import React, { useCallback, useMemo } from 'react';

import moment from 'moment';

import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import HeaderMobile from 'components/HeaderMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Flight } from 'models/flight';
import { TravelDetails } from 'models/travelDetails';
import { TravelStatus } from 'models/travelStatus';
import TravelTag from 'screens/components/TravelRequested/components/TravelTag';
import { getFormattedPrice } from 'utils/currency';

import { Container, Card, Header, Divider, Group, Itinerary, ArrowIcon, Line, Total, Cards, Timeline } from './styles';

type CardHistoryProps = {
  dateHeader: Date;
  firstFlight?: Flight;
  lastFlight?: Flight;
  totalPrice: number;
  isOriginal: boolean;
  currencyCode: string;
  status?: TravelStatus;
  onDetails: () => void;
};
const CardHistory = ({
  dateHeader,
  firstFlight,
  lastFlight,
  totalPrice,
  isOriginal,
  currencyCode,
  status,
  onDetails,
}: CardHistoryProps) => {
  const { t } = useStoreTranslation();
  const formatDateTime = (date: Date) => moment(date).format('LLL');
  const formatDate = (date: string) => moment(date).format('ddd, DD MMM');
  const formatTime = (date: string) => moment(date).format('LT');
  const { departureAirport: departureCode, departureDate } = useMemo(() => {
    if (firstFlight) return firstFlight;
    return { departureAirport: undefined, departureDate: undefined };
  }, [firstFlight]);
  const { arrivalAirport: arrivalCode, arrivalDate: returnDate } = useMemo(() => {
    if (lastFlight) return lastFlight;
    return { arrivalAirport: undefined, arrivalDate: undefined };
  }, [lastFlight]);

  const formattedPrice = useCallback(
    (price: number) => {
      const { formatted } = getFormattedPrice({ value: price, currency: currencyCode, t });
      return formatted;
    },
    [currencyCode, t],
  );
  if (departureDate === undefined || returnDate === undefined) {
    return null;
  }
  const LineItem = ({ items, keyItem }: { items: string[]; keyItem: string }) => (
    <Line key={keyItem}>
      {items.map((item, idx) => (
        <span key={`${keyItem}-${idx}`}>{item}</span>
      ))}
    </Line>
  );
  return (
    <Card>
      <Header>
        {formatDateTime(dateHeader)}
        <Header.Detail>
          {status && <TravelTag status={String(status)} />}
          <Header.Button onClick={onDetails}>
            {t('travelDetails.bookingHistory.detail')} <Header.Chevron />
          </Header.Button>
        </Header.Detail>
      </Header>
      <Divider $dark />
      <Group>
        <Itinerary>
          {departureCode} <ArrowIcon /> {arrivalCode}
        </Itinerary>
        <LineItem
          keyItem={'departure-1'}
          items={[t('travelDetails.bookingHistory.departureDate'), formatDate(departureDate)]}
        />
        <LineItem
          keyItem={'departure-2'}
          items={[t('travelDetails.bookingHistory.departureTime'), formatTime(departureDate)]}
        />
        <Divider />
        <LineItem keyItem={'return-1'} items={[t('travelDetails.bookingHistory.returnDate'), formatDate(returnDate)]} />
        <LineItem keyItem={'return-2'} items={[t('travelDetails.bookingHistory.returnTime'), formatTime(returnDate)]} />
      </Group>
      <Divider $dark />
      <Total>
        <span>
          {isOriginal ? t('travelDetails.bookingHistory.totalOrigin') : t('travelDetails.bookingHistory.totalExchange')}
        </span>
        <span>{formattedPrice(totalPrice)}</span>
      </Total>
    </Card>
  );
};

type Props = {
  show: boolean;
  onClose: () => void;
  onBack: () => void;
  onDetails: (isExchange: boolean) => void;
  data: TravelDetails;
};
const BookingHistory = ({ show, onClose, onBack, data, onDetails }: Props) => {
  const { t } = useStoreTranslation();
  const status = useMemo(() => data.requester.status ?? TravelStatus.Reproved, [data.requester.status]);
  const isConfirmed = (data.air && data.air.flightExchangedConfirmed) ?? false;
  const isPending = (data.air && data.air.flightExchangedPending) ?? false;

  const travelStatus = useMemo(() => {
    if (isConfirmed) return TravelStatus.Confirmed;
    if (isPending) return TravelStatus.ExchangePending;
    return undefined;
  }, [isConfirmed, isPending]);
  const firstExchangedFlight = useMemo(() => {
    if (data.air && data.air.sectorsExchanged && data.air.sectorsExchanged[0].flights) {
      return data.air.sectorsExchanged[0].flights[0];
    }
    return undefined;
  }, [data.air]);
  const lastExchangedFlight = useMemo(() => {
    if (data.air && data.air.sectorsExchanged && data.air.sectorsExchanged[0].flights) {
      return data.air.sectorsExchanged[0].flights.last();
    }
    return undefined;
  }, [data.air]);
  const firstFlight = useMemo(() => {
    if (data.flightSectors && data.flightSectors[0].flights) {
      return data.flightSectors[0].flights[0];
    }
    return undefined;
  }, [data.flightSectors]);
  const lastFlight = useMemo(() => {
    if (data.flightSectors && data.flightSectors[0].flights) {
      return data.flightSectors[0].flights.last();
    }
    return undefined;
  }, [data.flightSectors]);

  return (
    <>
      <ApplicationOverlayContent sidebar show={show} onClose={onClose} backgroundColor="#f9f9f9">
        <HeaderMobile goBack={onBack} title={t('travelDetails.bookingHistory.title')} />
        <Container>
          <Timeline>
            {firstExchangedFlight && (
              <>
                <Timeline.Dot />
                <Timeline.Line />
              </>
            )}
            {data.requester.requestDate && firstFlight && <Timeline.Dot />}
          </Timeline>
          <Cards>
            {firstExchangedFlight && (
              <CardHistory
                dateHeader={data.requester.exchangeRequestDate ?? new Date()}
                firstFlight={firstExchangedFlight}
                lastFlight={lastExchangedFlight}
                totalPrice={data.prices.penaltyAmount ?? 0}
                currencyCode={data.prices.currencyCode}
                isOriginal={false}
                status={travelStatus ?? status}
                onDetails={() => onDetails(true)}
              />
            )}
            {data.requester.requestDate && firstFlight && (
              <CardHistory
                dateHeader={data.requester.requestDate}
                firstFlight={firstFlight}
                lastFlight={lastFlight}
                totalPrice={data.prices.total}
                currencyCode={data.prices.currencyCode}
                isOriginal={true}
                status={status}
                onDetails={() => onDetails(false)}
              />
            )}
          </Cards>
        </Container>
      </ApplicationOverlayContent>
    </>
  );
};

export default BookingHistory;
